/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * ------------------------------------------------------------------------------------------------------------
 * PGC SWIPER
 * ------------------------------------------------------------------------------------------------------------
 *
 * Inicializa Swiper.js (v11, via CDN) para os carrosséis de agrupamento de produtos.
 * O CDN só é carregado quando a página contém .pgc-swiper ou .pgc-swiper-selector.
 *
 * .pgc-swiper          → seção product-group (cards em overflow)
 * .pgc-swiper-selector → bloco product_group_selector no product-form
 */
(function () {
  var SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
  var SWIPER_JS  = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';

  function loadSwiper(callback) {
    var link  = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = SWIPER_CSS;
    document.head.appendChild(link);

    var script    = document.createElement('script');
    script.src    = SWIPER_JS;
    script.onload = callback;
    document.head.appendChild(script);
  }

  function initAll() {
    // ----------------------------------------------------------------
    // Carrossel da seção product-group
    // ----------------------------------------------------------------
    document.querySelectorAll('.pgc-swiper').forEach(function (el) {
      var cols      = parseInt(el.getAttribute('data-pgc-cols'), 10) || 4;
      var gap       = parseInt(el.getAttribute('data-pgc-gap'),  10) || 20;
      var hasArrows = el.getAttribute('data-pgc-arrows') === 'true';
      var prevBtn   = el.querySelector('.pgc-arrow--prev');
      var nextBtn   = el.querySelector('.pgc-arrow--next');
      var paginationEl = el.querySelector('.pgc-swiper__pagination');

      var config = {
        grabCursor: true,
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 12,
        pagination: paginationEl ? { el: paginationEl, clickable: true } : false,
        breakpoints: {}
      };

      config.breakpoints[741] = {
        slidesPerView: cols,
        slidesPerGroup: cols,
        spaceBetween: gap
      };

      if (hasArrows && prevBtn && nextBtn) {
        config.navigation = { prevEl: prevBtn, nextEl: nextBtn };
      }

      new Swiper(el, config);
    });

    // ----------------------------------------------------------------
    // Carrossel do bloco product_group_selector
    // ----------------------------------------------------------------
    document.querySelectorAll('.pgc-swiper-selector').forEach(function (el) {
      var gap       = parseInt(el.getAttribute('data-pgc-gap'),  10) || 8;
      var hasArrows = el.getAttribute('data-pgc-arrows') === 'true';
      var prevBtn   = el.querySelector('.pgc-arrow--prev');
      var nextBtn   = el.querySelector('.pgc-arrow--next');
      var paginationEl = el.querySelector('.pgc-swiper__pagination');

      var config = {
        grabCursor: true,
        slidesPerView: 'auto',
        spaceBetween: gap,
        freeMode: true,
        pagination: paginationEl ? { el: paginationEl, clickable: true } : false
      };

      if (hasArrows && prevBtn && nextBtn) {
        config.navigation = { prevEl: prevBtn, nextEl: nextBtn };
      }

      new Swiper(el, config);
    });
  }

  function init() {
    if (!document.querySelector('.pgc-swiper, .pgc-swiper-selector')) return;

    if (window.Swiper) {
      initAll();
    } else {
      loadSwiper(initAll);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT GROUP DYNAMIC VARIANT TITLE
 * ------------------------------------------------------------------------------------------------------------
 * 
 * Atualiza o título da variante dinamicamente no hover dos cards (desktop only).
 * Usado na seção de agrupamento de produtos e no bloco seletor quando dynamic_variant_title está ativado.
 */
(function () {
  function initDynamicVariantTitle() {
    // Detecta se é desktop (min-width: 750px)
    const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;

    // Para section product-group (usa container separado)
    const titleContainer = document.querySelector('[data-pgc-dynamic-title]');
    if (titleContainer) {
      const cards = document.querySelectorAll('.pgc-card[data-variant-title]');
      
      cards.forEach(card => {
        const variantTitle = card.getAttribute('data-variant-title');
        if (!variantTitle) return;

        card.addEventListener('mouseenter', () => {
          if (isDesktop()) {
            titleContainer.textContent = variantTitle;
            titleContainer.classList.add('pgc-dynamic-title--visible');
          }
        });

        card.addEventListener('mouseleave', () => {
          if (isDesktop()) {
            titleContainer.classList.remove('pgc-dynamic-title--visible');
            setTimeout(() => {
              if (!titleContainer.classList.contains('pgc-dynamic-title--visible')) {
                titleContainer.textContent = '';
              }
            }, 300);
          }
        });
      });
    }

    // Para bloco product_group_selector (atualiza rótulo existente)
    const labelElement = document.querySelector('[data-pgc-label]');
    if (labelElement) {
      const originalTitle = labelElement.getAttribute('data-original-title');
      const selectorItems = document.querySelectorAll('.pgc-selector__item[data-variant-title]');
      
      selectorItems.forEach(item => {
        const variantTitle = item.getAttribute('data-variant-title');
        if (!variantTitle) return;

        item.addEventListener('mouseenter', () => {
          if (isDesktop()) {
            labelElement.textContent = variantTitle;
            labelElement.style.transition = 'opacity 0.2s ease';
          }
        });

        item.addEventListener('mouseleave', () => {
          if (isDesktop()) {
            labelElement.textContent = originalTitle;
          }
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDynamicVariantTitle);
  } else {
    initDynamicVariantTitle();
  }
})();

/**
 * ------------------------------------------------------------------------------------------------------------
 * MENU SIZE FILTER - COLLECTION FILTER MAPPING
 * ------------------------------------------------------------------------------------------------------------
 *
 * Mapeia valores de tamanho para parâmetros de filtro reais da Shopify.
 * Usa alternate template (?view=size-filters) para obter dados de collection.filters via Liquid.
 * Suporta tanto filtros de opção (filter.v.option.*) quanto taxonomia (filter.v.t.shopify.*).
 */
class MenuSizeFilterMapper {
  constructor() {
    this.cache = new Map();
    this.initializeLinks();
  }

  initializeLinks() {
    document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('[data-size-filter-chip]').forEach(chip => {
        chip.addEventListener('click', (e) => this.handleClick(e));
      });
    });
  }

  async handleClick(event) {
    event.preventDefault();

    const link = event.currentTarget;
    const collectionHandle = link.dataset.collectionHandle;
    const sizeValue = link.dataset.sizeValue;
    const optionName = link.dataset.sizeOptionName || '';
    const baseUrl = link.href;

    if (!collectionHandle || !sizeValue) {
      window.location.href = baseUrl;
      return;
    }

    try {
      let filterData = this.cache.get(collectionHandle);

      if (!filterData) {
        filterData = await this.fetchCollectionFilters(collectionHandle, optionName);
        this.cache.set(collectionHandle, filterData);
      }

      const match = filterData.sizeMap.get(sizeValue);

      if (match) {
        window.location.href = `${baseUrl}?${match.paramName}=${encodeURIComponent(match.value)}&filter.v.availability=1`;
      } else {
        this.fallbackRedirect(baseUrl, optionName, sizeValue);
      }
    } catch (error) {
      console.error('MenuSizeFilter: erro ao mapear filtro', error);
      this.fallbackRedirect(baseUrl, optionName, sizeValue);
    }
  }

  fallbackRedirect(baseUrl, optionName, sizeValue) {
    if (optionName) {
      const paramName = `filter.v.option.${optionName.toLowerCase().replace(/\s+/g, '-')}`;
      window.location.href = `${baseUrl}?${paramName}=${encodeURIComponent(sizeValue)}&filter.v.availability=1`;
    } else {
      window.location.href = `${baseUrl}?filter.v.availability=1`;
    }
  }

  async fetchCollectionFilters(collectionHandle, optionName) {
    const sizeKeywords = ['size', 'tamanho', 'shoe', 'calçado', 'talla', 'numero', 'número'];

    if (optionName) {
      sizeKeywords.push(optionName.toLowerCase());
    }

    try {
      const response = await fetch(`/collections/${collectionHandle}?view=size-filters`);
      const text = await response.text();
      const filters = JSON.parse(text.trim());

      const sizeMap = new Map();

      for (const filter of filters) {
        const labelLower = filter.label.toLowerCase();
        const paramLower = filter.param_name.toLowerCase();

        const isSize = sizeKeywords.some(kw =>
          labelLower.includes(kw) || paramLower.includes(kw)
        );

        if (isSize) {
          for (const val of filter.values) {
            sizeMap.set(val.label, {
              paramName: filter.param_name,
              value: val.value
            });
          }
          break;
        }
      }

      return { sizeMap };
    } catch (error) {
      console.error('MenuSizeFilter: erro ao buscar filtros JSON', error);
      return { sizeMap: new Map() };
    }
  }
}

// Inicializa o mapeador
if (typeof window !== 'undefined') {
  window.menuSizeFilterMapper = new MenuSizeFilterMapper();
}

(function () {
  const sizeNames = [
    'tamanho',
    'size',
    'talla',
    'numero',
    'pointure',
    'taglia',
    'grosse',
    'storlek',
    'koko',
    'サイズ',
    'مقاس'
  ];
  const apparelOrder = new Map([
    ['XXXS', 10], ['3XS', 10],
    ['XXS', 20], ['2XS', 20],
    ['PP', 30], ['XS', 30],
    ['P', 40], ['S', 40],
    ['M', 50],
    ['G', 60], ['L', 60],
    ['GG', 70], ['XL', 70],
    ['XG', 80], ['EG', 80], ['XXL', 80], ['2XL', 80],
    ['XGG', 90], ['EGG', 90], ['XXXL', 90], ['3XL', 90],
    ['4XL', 100], ['5XL', 110], ['6XL', 120]
  ]);
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  let frameRequest = null;

  function normalize(value) {
    return String(value)
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  }

  function compact(value) {
    return normalize(value).replace(/[^A-Z0-9]/g, '');
  }

  function isSizeOption(name) {
    const normalizedName = normalize(name).toLowerCase();
    return sizeNames.some((sizeName) => normalizedName.includes(sizeName));
  }

  function sortKey(value) {
    const normalizedValue = normalize(value);
    const compactValue = compact(value);

    if (apparelOrder.has(compactValue)) {
      return [1, apparelOrder.get(compactValue), normalizedValue];
    }

    const numericMatch = normalizedValue.replace(',', '.').match(/\d+(?:\.\d+)?/);
    if (numericMatch) {
      return [0, Number(numericMatch[0]), normalizedValue];
    }

    const uniqueValues = ['UNICO', 'UNICA', 'ONE SIZE'];
    if (uniqueValues.includes(normalizedValue)) {
      return [2, 0, normalizedValue];
    }

    return [3, 0, normalizedValue];
  }

  function valueFromElement(element) {
    return element.value || element.getAttribute('value') || element.textContent.trim();
  }

  function compareSizeElements(firstElement, secondElement) {
    const firstKey = sortKey(valueFromElement(firstElement));
    const secondKey = sortKey(valueFromElement(secondElement));

    if (firstKey[0] !== secondKey[0]) return firstKey[0] - secondKey[0];
    if (firstKey[1] !== secondKey[1]) return firstKey[1] - secondKey[1];
    return collator.compare(firstKey[2], secondKey[2]);
  }

  function sortContainer(container) {
    const sortedElements = Array.from(container.children).sort(compareSizeElements);

    sortedElements.forEach((element, index) => {
      element.style.order = String(index + 1);
    });
  }

  function sortSizeOptions(root) {
    root.querySelectorAll('[data-size-option-list]').forEach(sortContainer);

    root
      .querySelectorAll('.product-form__option-selector, .product-sticky-form__variants .select-wrapper')
      .forEach((optionElement) => {
        const nameElement = optionElement.querySelector('.product-form__option-name, .combo-box__title');
        if (!nameElement || !isSizeOption(nameElement.textContent)) return;

        optionElement
          .querySelectorAll('.variant-swatch-list, .color-swatch-list, .block-swatch-list, .combo-box__option-list, select')
          .forEach(sortContainer);
      });
  }

  function scheduleSort() {
    if (frameRequest !== null) return;

    frameRequest = window.requestAnimationFrame(() => {
      frameRequest = null;
      sortSizeOptions(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSort, { once: true });
  } else {
    scheduleSort();
  }

  new MutationObserver(scheduleSort).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
